import { AuthContext } from '@/auth/context';
import { GoogleLoginButton } from '@/components/google-login';
import { LoadingPage } from '@/components/loading-page';
import { useCsrf } from '@/lib/csrf-context';
import { Navigate, createFileRoute } from '@tanstack/react-router';
import { AlertCircle, LoaderIcon } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { isCoachOffboarded } from '@/lib/coach-utils';
import { LoginLayout } from './_layout/-components/login-layout';

export const Route = createFileRoute('/login')({
  component: LoginComponent,
});

function LoginComponent() {
  const { csrfToken } = useCsrf();
  const { loggedInUser, loading: isLoadingAuthState, identityProviderUser, logout } = useContext(AuthContext);
  const router = useRouter();
  const { redirect } = Route.useSearch<{ redirect?: string }>();

  // Check if the provided route exists
  const validRedirect = useMemo(() => {
    if (!redirect) {
      return null;
    }

    try {
      const matches = router.matchRoutes(redirect, {
        strictParseParams: false,
        includeRedirects: false,
      });

      if (matches.length > 0 && matches.some(({ pathname }) => pathname === redirect || pathname === `${redirect}/`)) {
        return redirect;
      }

      return null;
    } catch {
      return null;
    }
  }, [redirect, router]);

  const redirectInProgress = typeof window !== 'undefined' && sessionStorage.getItem('auth:redirectInProgress') === '1';

  if (!csrfToken || isLoadingAuthState || redirectInProgress) {
    return <LoadingPage />;
  }

  // Check if user is a coach and it has status offboarded
  if (loggedInUser && isCoachOffboarded(loggedInUser)) {
    return (
      <LoginLayout>
        <div className="mb-12 space-y-6 text-left md:text-center w-full">
          <img src="/logo.svg" className="w-40 md:w-48 md:mx-auto" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-air-primary">Access Denied</h1>
          <Alert variant="destructive" className="mb-6 text-left">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Account Offboarded</AlertTitle>
            <AlertDescription>
              Your account has been offboarded and you no longer have access to this application. If you believe this is
              an error, please contact support.
            </AlertDescription>
          </Alert>
        </div>
        <div className="flex flex-col items-center w-full">
          <Button
            onClick={() => logout()}
            variant={'destructive'}
            className="w-full border-2 rounded-lg px-6 py-4 sm:px-8 sm:py-6 font-semibold leading-tight"
          >
            Sign Out
          </Button>
        </div>
      </LoginLayout>
    );
  }

  if (loggedInUser) {
    const targetRoute = validRedirect || '/';
    return <Navigate to={targetRoute} replace />;
  }

  return (
    <LoginLayout>
      <div className="mb-12 space-y-6 text-left md:text-center w-full">
        <img src="/logo.svg" className="w-40 md:w-48 md:mx-auto" />
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-air-primary">
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            <span>Welcome</span>
            <span aria-hidden="true">👋</span>
          </span>
        </h1>
      </div>
      <div className="flex flex-col items-center w-full">
        <div className="w-full max-w-[463px]">{identityProviderUser ? <LoaderIcon /> : <GoogleLoginButton />}</div>
      </div>
    </LoginLayout>
  );
}
